import React, { useState } from 'react';
import {
  Modal,
  Stepper,
  FileInput,
  Button,
  Group,
  Table,
  Text,
  Badge,
  Tooltip,
  Stack,
} from '@mantine/core';
import { IconUpload, IconCheck, IconAlertCircle } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import {
  useImportPreview,
  useImportExecute,
  downloadTemplate,
  type PreviewResult,
  type ImportResult,
} from '../../hooks/useImportExportApi';

interface ImportModalProps {
  opened: boolean;
  onClose: () => void;
  importUrl: string;
  templateUrl: string;
  title: string;
  onSuccess: () => void;
}

export const ImportModal: React.FC<ImportModalProps> = ({
  opened,
  onClose,
  importUrl,
  templateUrl,
  title,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const [step, setStep] = useState(0);
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<PreviewResult | null>(null);
  const [result, setResult] = useState<ImportResult | null>(null);

  const previewMutation = useImportPreview();
  const importMutation = useImportExecute();

  const handleCheck = async () => {
    if (!file) return;
    const data = await previewMutation.mutateAsync({ url: importUrl, file }).catch(() => null);
    if (!data) return;
    setPreview(data);
    setStep(1);
  };

  const handleImport = async () => {
    if (!file) return;
    const data = await importMutation.mutateAsync({ url: importUrl, file }).catch(() => null);
    if (!data) return;
    setResult(data);
    setStep(2);
  };

  const handleClose = () => {
    previewMutation.reset();
    importMutation.reset();
    setStep(0);
    setFile(null);
    setPreview(null);
    setResult(null);
    onClose();
  };

  const handleDone = () => {
    onSuccess();
    handleClose();
  };

  return (
    <Modal opened={opened} onClose={handleClose} title={title} size="lg">
      <Stepper active={step} size="sm" mb="md">
        <Stepper.Step label={t('importExport.uploadFile')} />
        <Stepper.Step label={t('importExport.preview')} />
        <Stepper.Step label={t('importExport.done')} />
      </Stepper>

      {step === 0 && (
        <Stack gap="md">
          <FileInput
            label={t('importExport.uploadFile')}
            placeholder={t('importExport.uploadFile')}
            accept=".csv,.xlsx"
            leftSection={<IconUpload size={16} />}
            value={file}
            onChange={setFile}
          />
          <Group gap="xs">
            <Text size="sm" c="dimmed">
              {t('importExport.downloadTemplate')}:
            </Text>
            <Button variant="subtle" size="xs" onClick={() => downloadTemplate(templateUrl, 'csv')}>
              CSV
            </Button>
            <Button
              variant="subtle"
              size="xs"
              onClick={() => downloadTemplate(templateUrl, 'xlsx')}
            >
              Excel
            </Button>
          </Group>
          {previewMutation.error && (
            <Text size="sm" c="red">
              {previewMutation.error.message}
            </Text>
          )}
          <Button onClick={handleCheck} disabled={!file} loading={previewMutation.isPending}>
            {t('importExport.check')}
          </Button>
        </Stack>
      )}

      {step === 1 && preview && (
        <Stack gap="md">
          <Group>
            <Badge color="teal" size="lg">
              {preview.summary.valid} {t('importExport.valid')}
            </Badge>
            {preview.summary.invalid > 0 && (
              <Badge color="red" size="lg">
                {preview.summary.invalid} {t('importExport.invalid')}
              </Badge>
            )}
          </Group>

          <Table striped highlightOnHover withTableBorder>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>#</Table.Th>
                {preview.rows[0] &&
                  Object.keys(preview.rows[0].data).map((key) => (
                    <Table.Th key={key}>{key}</Table.Th>
                  ))}
                <Table.Th></Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {preview.rows.map((row) => (
                <Table.Tr
                  key={row.row}
                  bg={row.isValid ? undefined : 'var(--mantine-color-red-light)'}
                >
                  <Table.Td>{row.row}</Table.Td>
                  {Object.values(row.data).map((val, i) => (
                    <Table.Td key={i}>{val}</Table.Td>
                  ))}
                  <Table.Td>
                    {!row.isValid ? (
                      <Tooltip
                        label={row.errors
                          .map((e) => `${e.field}: ${t(`importExport.errors.${e.code}`)}`)
                          .join(', ')}
                      >
                        <IconAlertCircle
                          size={16}
                          color="var(--mantine-color-red-6)"
                          aria-label={`${t('importExport.rowErrors')} ${row.row}`}
                        />
                      </Tooltip>
                    ) : row.isExisting ? (
                      <Tooltip label={t('importExport.existingHint')}>
                        <Badge size="xs" variant="light">
                          {t('importExport.existing')}
                        </Badge>
                      </Tooltip>
                    ) : (
                      <IconCheck size={16} color="var(--mantine-color-teal-6)" />
                    )}
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>

          {importMutation.error && (
            <Text size="sm" c="red">
              {importMutation.error.message}
            </Text>
          )}

          <Group justify="space-between">
            <Button variant="light" onClick={() => setStep(0)}>
              {t('common.back')}
            </Button>
            <Button
              onClick={handleImport}
              disabled={preview.summary.valid === 0}
              loading={importMutation.isPending}
            >
              {t('importExport.importBtn')}
            </Button>
          </Group>
        </Stack>
      )}

      {step === 2 && result && (
        <Stack gap="md" align="center">
          <IconCheck size={48} color="var(--mantine-color-teal-6)" />
          <Text size="lg" fw={600}>
            {result.imported} {t('importExport.imported')}
          </Text>
          {!!result.updated && (
            <Text size="sm">
              {t('importExport.updated')}: {result.updated}
            </Text>
          )}
          {result.skipped > 0 && (
            <Text size="sm" c="dimmed">
              {t('importExport.skipped')}: {result.skipped}
            </Text>
          )}
          <Button onClick={handleDone}>{t('importExport.done')}</Button>
        </Stack>
      )}
    </Modal>
  );
};
